import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AuthComponent } from '../../../modules/auth/components/auth-modal/auth.component';
import { Router } from '@angular/router';
import {
  AuthDialogMethod,
  AUTHORIZATION_STATE,
} from '../../../modules/auth/models/auth.models';
import { UserService } from '../../services/user.service';
import { distinctUntilChanged, map, Observable, tap } from 'rxjs';
import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { SupabaseUserToolsService } from '../../services/supabase-user-tools.service';
import { ProfileService } from '../../../modules/profile/profile-edit/profile.service';
import { MatMenuModule } from '@angular/material/menu';
import { StyleService } from '../../services/style.service';
import { TranslationService } from '../../services/translation.service';
import { TranslatePipe } from 'src/app/pipes/translate.pipe';
import { ConfigurationService } from '../../configuration/configuration.service';
import {
  CLIENT_NAME,
  CLIENT_NAMES,
} from '../../configuration/configuration.model';
import { DrawerService } from '../../services/drawer.service';
import { AnalyticsService } from '../../services/analytics.service';
import { MatBadgeModule } from '@angular/material/badge';
import { NotificationsService } from '../../services/notifications.service';
import { PushNotificationService } from '../../services/push-notification.service';
import { Notification } from 'src/app/shared/models/notification.model';
import { ClubReservationSettings } from '../../models/clubs.models';
import { ClubService } from '../../services/club.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DependentService } from '../../services/dependent.service';
import { BillingNoticeService } from '../../services/billing-notice.service';
import { MatDividerModule } from '@angular/material/divider';

@Component({
  selector: 'app-header',
  imports: [
    MatIconModule,
    MatDialogModule,
    AsyncPipe,
    MatMenuModule,
    TranslatePipe,
    MatBadgeModule,
    MatProgressSpinner,
    MatDividerModule,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  unreadNotifications: Notification[] = [];
  showNotifications = false;
  public email: string = '';
  public isLoggedIn = this.userService.isLoggedIn.pipe(
    map((authState) => authState === AUTHORIZATION_STATE.LOGGED_IN)
  );
  public isMobile = this.styleService.getIfIsMobile();
  public isUserAdmin = false;
  public isUserVerified = false;
  public isUserLoggedIn: Observable<boolean> | undefined;
  public userInfoUpdated = false;
  public clientName!: string;

  notifCount: number = 0;
  public logo_URL!: string | '';

  public readonly dependentService = inject(DependentService);
  /** Actively's payment warning, admins only */
  public readonly billingNotice = inject(BillingNoticeService);
  private readonly pushNotificationService = inject(PushNotificationService);

  get familyEnabled(): boolean {
    return this.clubsService.flags()?.family === true;
  }

  constructor(
    private userService: UserService,
    private supabaseUserToolsService: SupabaseUserToolsService,
    private profileService: ProfileService,
    public router: Router,
    private styleService: StyleService,
    private translationService: TranslationService,
    private configurationService: ConfigurationService,
    protected drawerService: DrawerService,
    private analytics: AnalyticsService,
    private notificationsService: NotificationsService,
    private clubsService: ClubService
  ) {}

  async ngOnInit() {
    this.clientName = this.configurationService.getClientName();
    this.logo_URL = await this.clubsService.getLogoRelativePath();

    this.supabaseUserToolsService.userEmailSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((email) => {
        this.email = email;
      });
    this.isUserLoggedIn = this.userService.isLoggedIn.pipe(
      map((res) => res === AUTHORIZATION_STATE.LOGGED_IN)
    );

    await this.updateUserDetails();

    this.supabaseUserToolsService.isUserAdmin$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isAdmin) => {
        this.isUserAdmin = isAdmin;
        if (isAdmin) void this.billingNotice.load();
      });

    this.supabaseUserToolsService.isUserVerified$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isVerified) => {
        this.isUserVerified = isVerified;
      });

    this.supabaseUserToolsService.updateUserStatus();

    const savedLang = localStorage.getItem('language');

    if (savedLang) {
      this.translationService.setLanguage(savedLang);
    } else {
      this.translationService.setLanguage('gr');
      localStorage.setItem('language', 'gr');
    }

    this.notificationsService.unreadCount
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => {
        this.notifCount = count;
      });

    // Load dependents for profile switcher and trigger initial notification fetch
    // Always load — the RPC returns empty if no dependents exist.
    // The UI is already gated behind familyEnabled.
    this.userService.isLoggedIn
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        if (state === AUTHORIZATION_STATE.LOGGED_IN) {
          this.dependentService.loadDependents();
          this.notificationsService.fetchUnreadCount();
          // Re-register this device's push token once per session.
          void this.pushNotificationService.syncSubscription();
        }
      });
  }

  switchProfile(userId: string): void {
    this.dependentService.switchToProfile(userId);
  }

  switchToSelf(): void {
    console.log('[Header] switchToSelf called');
    this.dependentService.switchToSelf();
    console.log('[Header] isActingAsDependent:', this.dependentService.isActingAsDependent());
  }

  public openDialog() {
    const dialogRef = this.dialog.open(AuthComponent, {
      width: '440px',
      maxWidth: '95vw',
      panelClass: 'auth-dialog',
      data: {
        method: AuthDialogMethod.LOGIN,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(
        tap(() => {
          this.supabaseUserToolsService.updateUserStatus();
        })
      )
      .subscribe();
  }

  async toggleNotifications() {
    this.router.navigate(['notifications']);
  }

  openRegisterDialog() {
    const dialogRef = this.dialog.open(AuthComponent, {
      width: '440px',
      maxWidth: '95vw',
      panelClass: 'auth-dialog',
      data: {
        method: AuthDialogMethod.REGISTER,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(
        tap(() => {
          this.supabaseUserToolsService.updateUserStatus();
        })
      )
      .subscribe();
  }

  async logout() {
    // While the session still exists: this device stops getting the member's pushes.
    await this.pushNotificationService.releaseDevice();
    await this.userService.clearSession();
    await this.updateUserDetails();
    await this.supabaseUserToolsService.updateUserStatus();
  }

  private async updateUserDetails() {
    this.isUserAdmin = await this.supabaseUserToolsService.isUserAdmin();
    this.isUserVerified =
      (await this.supabaseUserToolsService.isUserVerified()) ?? false;
    const user = await this.userService.getUserPromise();
    if (!user.error && user.data.user?.id) {
      this.userInfoUpdated =
        !(await this.profileService.checkIfUserHasFullfiledPersonalInfo(
          user.data.user?.id
        ));
    } else {
      this.userInfoUpdated = false;
    }
  }

  toggleDrawer() {
    this.drawerService.toggle();
  }

  profileSettings() {
    this.router.navigate(['profileSettings']);
  }
}
